"use client";

import clsx from "clsx";
import {usePathname} from "next/navigation";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {resolveRouteFromPathname} from "@/data/routeRegistry";
import ArtifactPanel from "@/components/chat/ArtifactPanel";
import ChatViewContainer from "@/components/chat/ChatViewContainer";
import Button from "@/components/library/Button";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {CHANNEL_TYPE} from "@/enums/ai";
import {useBrandConfig} from "@/hooks/useBrandConfig";
import {useOrg} from "@/hooks/useOrg";
import GailGptConversations, {type GailGptConversation} from "@/services/gailgpt/conversations";
import {GetGailGPTBrandName} from "@/utils/chat";
import {useRouter} from "@bprogress/next";
import GailConversationWelcome from "./GailConversationWelcome";

const headerIconButtonClassName =
   "!inline-flex !h-8 !w-8 !rounded-md !p-0 text-700 hover:text-900 hover:bg-200";

const HeaderIconButton: React.FC<{
   icon: React.ComponentProps<typeof Icon>["icon"];
   label: string;
   onClick: () => void;
}> = ({icon, label, onClick}) => (
   <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
         <Button
            type="none"
            compact
            iconOnly
            onClick={onClick}
            aria-label={label}
            className={headerIconButtonClassName}
         >
            <Icon icon={icon} size={ICON_SIZES.SM} />
         </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6}>
         <p>{label}</p>
      </TooltipContent>
   </Tooltip>
);

const PageViewingIndicator: React.FC = () => {
   const {t} = useTranslation("ask");
   const pathname = usePathname();
   const route = resolveRouteFromPathname(pathname ?? "");

   if (!route?.name) return null;

   const label = t("pageContext.viewing", {page: route.name});

   return (
      <Tooltip delayDuration={100}>
         <TooltipTrigger asChild>
            <Button type="none" compact iconOnly aria-label={label} className={headerIconButtonClassName}>
               <Icon icon="eye" size={ICON_SIZES.SM} />
            </Button>
         </TooltipTrigger>
         <TooltipContent side="bottom" sideOffset={6}>
            <p>{label}</p>
         </TooltipContent>
      </Tooltip>
   );
};

type ConversationControls = {
   conversationId: string | null;
   onSelectConversation: (id: string) => void;
   onClose: () => void;
   onNewChat: () => void;
};

const HistoryDropdown = ({conversationId, onSelectConversation, onClose}: Omit<ConversationControls, "onNewChat">) => {
   const {t} = useTranslation("ask");
   const {t: at} = useTranslation("assistants");
   const {t: ct} = useTranslation("common");
   const {user} = useAuthContext();
   const router = useRouter();
   const [open, setOpen] = useState(false);
   const [conversations, setConversations] = useState<GailGptConversation[] | null>(null);
   const [loadError, setLoadError] = useState(false);
   const [retryCount, setRetryCount] = useState(0);

   useEffect(() => {
      if (!open) return;
      let cancelled = false;
      setConversations(null);
      setLoadError(false);
      void (async () => {
         try {
            const response = await GailGptConversations.getConversations({
               type: CHANNEL_TYPE.GPT,
               size: 12,
               sortBy: "LastModifiedAt",
               userId: user?.userId,
            });
            if (!response.success || !response.data || !Array.isArray(response.data.data)) {
               throw new Error("Could not load conversation history");
            }
            if (!cancelled) setConversations(response.data.data);
         } catch {
            if (!cancelled) {
               setConversations([]);
               setLoadError(true);
            }
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [open, user?.userId, retryCount]);

   return (
      <Popover open={open} onOpenChange={setOpen}>
         <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
               <PopoverTrigger asChild>
                  <Button
                     type="none"
                     compact
                     iconOnly
                     aria-label={t("dock.history")}
                     className={clsx(headerIconButtonClassName, "data-[state=open]:bg-200")}
                  >
                     <Icon icon="clock-rotate-left" size={ICON_SIZES.SM} />
                  </Button>
               </PopoverTrigger>
            </TooltipTrigger>
            {!open && (
               <TooltipContent side="bottom" sideOffset={6}>
                  <p>{t("dock.history")}</p>
               </TooltipContent>
            )}
         </Tooltip>
         <PopoverContent portalToBody side="bottom" align="end" sideOffset={6} className="w-72 p-2">
            <p className="px-2 pb-1.5 pt-0.5 text-xs font-medium text-600">{t("dock.history")}</p>
            {!conversations && !loadError && (
               <div className="grid gap-1 px-2 py-1">
                  {[0, 1, 2].map((i) => (
                     <div key={i} className="h-7 animate-pulse rounded-md bg-200/70" />
                  ))}
               </div>
            )}
            {loadError && (
               <div role="alert" className="px-2 py-2 text-sm text-600">
                  <p>{at("historyLoadFailed")}</p>
                  <button
                     type="button"
                     onClick={() => setRetryCount((count) => count + 1)}
                     className="mt-2 font-medium text-gailPrimary hover:underline"
                  >
                     {ct("retry")}
                  </button>
               </div>
            )}
            {!loadError && conversations?.length === 0 && (
               <p className="px-2 py-2 text-sm text-600">{t("dock.noHistory")}</p>
            )}
            {!loadError && conversations && conversations.length > 0 && (
               <div className="grid max-h-80 gap-0.5 overflow-y-auto">
                  {conversations.map((conversation) => (
                     <button
                        key={conversation.id}
                        type="button"
                        onClick={() => {
                           onSelectConversation(conversation.id);
                           setOpen(false);
                        }}
                        className={clsx(
                           "w-full truncate rounded-lg px-3 py-1 text-left text-sm",
                           conversation.id === conversationId
                              ? "font-medium text-gailPrimary bg-gailPrimary/10"
                              : "text-800 hover:bg-200"
                        )}
                     >
                        {conversation.name || t("dock.untitledConversation")}
                     </button>
                  ))}
               </div>
            )}
            <div className="mt-1 border-t border-neutral pt-1">
               <button
                  type="button"
                  onClick={() => {
                     setOpen(false);
                     onClose();
                     router.push("/history");
                  }}
                  className="w-full rounded-lg px-3 py-1 text-left text-sm text-700 hover:bg-200"
               >
                  {t("dock.viewAllHistory")}
               </button>
            </div>
         </PopoverContent>
      </Popover>
   );
};

export type GailConversationPanelProps = ConversationControls & {
   overlayArtifacts?: boolean;
   onReviewOpenChange?: (open: boolean) => void;
   /** `sheet` is the quiet corner panel: plus "New chat" icon, no brand title. */
   chrome?: "dock" | "sheet";
};

/** Shared conversation UI; the host owns placement and conversation selection. */
const GailConversationPanel = ({
   conversationId,
   onSelectConversation,
   onClose,
   onNewChat,
   overlayArtifacts = false,
   onReviewOpenChange,
   chrome = "dock",
}: GailConversationPanelProps) => {
   const {t} = useTranslation("ask");
   const {t: rt} = useTranslation("routes");
   const {org, ui} = useOrg();
   const {config: brandConfig} = useBrandConfig(ui.brand);
   const router = useRouter();

   const brandName = GetGailGPTBrandName(ui.brand, rt, brandConfig);
   const sheet = chrome === "sheet";
   const {activeArtifact} = useArtifactPanel();
   const reviewing = overlayArtifacts && activeArtifact?.kind === "approval";
   useLayoutEffect(() => {
      onReviewOpenChange?.(reviewing);
      return () => onReviewOpenChange?.(false);
   }, [reviewing, onReviewOpenChange]);
   const showArtifact = overlayArtifacts && !!activeArtifact;
   const conversationFocusRef = useRef<HTMLElement | null>(null);

   const openFullView = () => {
      router.push(conversationId ? `/gailgpt/${conversationId}` : "/gailgpt");
   };

   return (
      <div className="gail-chat-sidebar relative h-full">
         <div className="flex h-full flex-col" inert={showArtifact} aria-hidden={showArtifact}
            onFocusCapture={event => { conversationFocusRef.current = event.target; }}>
            <header
               className={clsx(
                  "flex items-center gap-1 flex-shrink-0",
                  sheet ? "h-12 px-2" : "pl-4 pr-2 py-2"
               )}
            >
               {sheet ? (
                  <span className="flex-1" />
               ) : (
                  <p className="flex-1 min-w-0 text-sm font-semibold text-900 truncate">{brandName}</p>
               )}
               {!sheet && <PageViewingIndicator />}
               {/* On the welcome screen there is nothing to leave, so the action would do nothing. */}
               {conversationId && (
                  <HeaderIconButton
                     icon={sheet ? "plus" : "pen-to-square"}
                     label={t("dock.newChat")}
                     onClick={onNewChat}
                  />
               )}
               <HistoryDropdown conversationId={conversationId} onSelectConversation={onSelectConversation} onClose={onClose} />
               <HeaderIconButton
                  icon="arrow-up-right-from-square"
                  label={t("dock.openFullView")}
                  onClick={openFullView}
               />
               <HeaderIconButton icon="xmark" label={t("dock.close")} onClick={onClose} />
            </header>

            <div className="flex-1 min-h-0">
               {conversationId ? (
                  <ChatViewContainer key={conversationId} orgId={org?.id} conversationId={conversationId} fillContainer sheet={sheet} />
               ) : (
                  <GailConversationWelcome
                     orgId={org?.id}
                     onConversationCreated={onSelectConversation}
                     presentation={sheet ? "sheet" : "greeting"}
                  />
               )}
            </div>
         </div>
         {showArtifact && <div className="absolute inset-0 z-10 overflow-hidden bg-panel"><ArtifactPanel returnFocusRef={conversationFocusRef} /></div>}
      </div>
   );
};

export default GailConversationPanel;
